import { X } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

const FOCAVEIS = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Folhas abertas, da mais antiga para a mais nova: Esc e Tab valem só para a de cima.
const abertas: object[] = [];

/**
 * Painel sobre a tela: preso à direita, de altura inteira, no desktop/tablet; de baixo para cima no celular.
 * Fecha com Esc, com o botão ou tocando fora. O Tab circula dentro da folha e, ao fechar,
 * o foco volta para quem a abriu.
 */
export function Folha({
  titulo,
  subtitulo,
  aoFechar,
  children,
  rodape,
}: {
  titulo: string;
  subtitulo?: ReactNode;
  aoFechar: () => void;
  children: ReactNode;
  rodape?: ReactNode;
}) {
  const painel = useRef<HTMLDivElement>(null);
  // Lido no primeiro desenho, antes que um campo com autoFocus da folha tome o foco.
  const [gatilho] = useState(() => document.activeElement as HTMLElement | null);
  const fechar = useRef(aoFechar);
  useEffect(() => {
    fechar.current = aoFechar;
  });

  useEffect(() => {
    const esta = {};
    abertas.push(esta);
    const el = painel.current!;
    // Campo com autoFocus já está com o foco; sem ele, o painel recebe.
    if (!el.contains(document.activeElement)) el.focus();
    const tecla = (e: KeyboardEvent) => {
      if (abertas.at(-1) !== esta) return;
      if (e.key === "Escape") {
        e.preventDefault();
        fechar.current();
        return;
      }
      if (e.key !== "Tab") return;
      const focaveis = [...el.querySelectorAll<HTMLElement>(FOCAVEIS)].filter((x) => x.getClientRects().length > 0);
      const primeiro = focaveis[0];
      const ultimo = focaveis.at(-1);
      const atual = document.activeElement;
      if (!primeiro || !ultimo) {
        e.preventDefault();
        el.focus();
      } else if (!el.contains(atual)) {
        e.preventDefault();
        primeiro.focus();
      } else if (e.shiftKey && (atual === primeiro || atual === el)) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && atual === ultimo) {
        e.preventDefault();
        primeiro.focus();
      }
    };
    window.addEventListener("keydown", tecla);
    return () => {
      window.removeEventListener("keydown", tecla);
      abertas.splice(abertas.indexOf(esta), 1);
      if (gatilho?.isConnected) gatilho.focus();
    };
  }, [gatilho]);

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={titulo}>
      {/* O véu fecha com o toque; no teclado, Esc e o botão Fechar fazem o mesmo. */}
      <button type="button" tabIndex={-1} aria-hidden className="veu-entrada absolute inset-0 bg-black/30" onClick={aoFechar} />
      <div
        ref={painel}
        tabIndex={-1}
        className="sobreposta folha-entrada absolute inset-x-0 bottom-0 flex max-h-[92dvh] flex-col overflow-hidden rounded-t-[var(--raio-folha)] border-t border-borda bg-superficie outline-none md:inset-y-0 md:right-0 md:left-auto md:max-h-none md:w-[28rem] md:rounded-none md:border-t-0 md:border-l md:pt-[env(safe-area-inset-top)] xl:w-[30rem]"
      >
        <div className="flex items-start gap-3 px-5 pt-4 pb-3">
          <div className="min-w-0 flex-1">
            <h2 className="titulo-serifa text-[20px] font-semibold">{titulo}</h2>
            {subtitulo && <div className="text-sm text-texto-suave">{subtitulo}</div>}
          </div>
          <button type="button" onClick={aoFechar} className="botao botao-secundario botao-icone" aria-label="Fechar">
            <X className="size-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-4">{children}</div>
        {rodape && (
          <div className="border-t border-borda px-5 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:pb-4">{rodape}</div>
        )}
      </div>
    </div>
  );
}
